import React, { useEffect, useRef, useState } from "react";
import HoverVideoPlayer from "react-hover-video-player";
import {
  BsFillPlayFill,
  BsHourglassSplit,
  BsPinAngleFill,
  BsVolumeMute,
  BsVolumeUp,
} from "react-icons/bs";
import { Card } from "react-bootstrap";

import "../StayUpToDateComponent/StayUpToDate.css";
import "./ShopCardAlignment.css";

import ProductName from "../ProductName/ProductName";
import ProductShopButton from "../ProductName/ProductShopButton";
import {
  getMediaSrc,
  redirectToUrl,
  openOutboundUrl,
} from "../../Utils/StayUpToDate/RenderComponent";
import { convertToWebP } from "../../Utils/imageUtils";
import { normalizeSoundValue } from "../../Utils/helpers/carouselHelper";
import {
  DUMMY_VIDEO_POSTER,
  isLikelyVideoUrl,
  listIndexedVideoPosterUrls,
  listVideoPosterUrls,
  resolvePlayableVideoUrl,
} from "../../Utils/media/feedMedia";
import {
  useTemplateMediaIndex,
  useTemplateMediaReveal,
} from "../../Screens/DashboardView/MasterTemplateEditor/useTemplateMediaReveal";
import "../../Screens/DashboardView/MasterTemplateEditor/templateMediaReveal.css";

const IndividualVideoPostTag = (props: any) => {
  const {
    post,
    title,
    handleRedirect,
    isLinks,
    brand_color,
    isYoutube,
    isHasMediaUrl,
    thumbnail,
    productName,
    type,
    contentShape,
    isSoundOn: isSoundOnProp,
    from,
    designTokens,
    pinnedPostIds = [],
    isCarousel = false,
    mediaIndex,
  } = props;

  const isSoundOn = normalizeSoundValue(isSoundOnProp, true);

  return (
    <VideoTag
      media_url={
        resolvePlayableVideoUrl({
          mediaUrl: props.media_url || post?.media_url,
          storageUrl: post?.storage_url,
          type: type || post?.type,
          platform: post?.platform,
        }) ||
        props.media_url ||
        post?.media_url
      }
      post={post}
      thumbnail={thumbnail}
      mediaIndex={mediaIndex}
      brand_color={brand_color}
      isYoutube={isYoutube}
      handleRedirect={handleRedirect}
      title={title}
      isLinks={isLinks}
      isHasMediaUrl={isHasMediaUrl}
      productName={productName}
      type={type}
      contentShape={contentShape}
      isSoundOn={isSoundOn}
      from={from}
     designTokens={designTokens}
      pinnedPostIds={pinnedPostIds}
      isCarousel={isCarousel}
    />
  );
};

/** Feed-level “which post is playing” so sibling tiles reset their chrome. */
const ACTIVE_VIDEO_EVENT = "mte-active-video-post";

const VideoTag = (props: any) => {
  const {
    media_url,
    brand_color,
    post,
    isYoutube,
    thumbnail,
    mediaIndex,
    title,
    isLinks,
    isHasMediaUrl,
    productName,
    type,
    contentShape,
    isSoundOn,
    from,
  designTokens,
    pinnedPostIds = [],
    handleRedirect,
    isCarousel = false,
  } = props;

  const playableSrc =
    resolvePlayableVideoUrl({
      mediaUrl: media_url || post?.media_url,
      storageUrl: post?.storage_url,
      type: type || post?.type,
      platform: post?.platform,
    }) ||
    media_url ||
    post?.media_url;
  const postKey = String(post?.post_id || post?.table_id || "").trim();
  const isPinned = pinnedPostIds?.includes(postKey);

  const videoRef: any = useRef();
  const hoverTargetRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState(false);
  /** Sticky play after first start — mute taps must not drop playback. */
  const [keepPlaying, setKeepPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [customPause, setCustomPause] = useState(false);
  const [showClickablePlayBtn, setShowClickablePlayBtn] = useState(false);
  const isTemplateSite = from === "website-template";
  const soundEnabled = Boolean(isSoundOn);
  const ctxIndex = useTemplateMediaIndex();
  // Same poster chain as LiB VideoPostTag — expired CDN thumbs must fall
  // through still slides / dummy instead of a broken &lt;img&gt;.
  const posterCandidates = (
    typeof mediaIndex === "number"
      ? listIndexedVideoPosterUrls(post, mediaIndex)
      : listVideoPosterUrls(post, thumbnail)
  ).map((url) => convertToWebP(url));
  const thumbnailSrc = posterCandidates[0] || DUMMY_VIDEO_POSTER;
  const [posterSrc, setPosterSrc] = useState(thumbnailSrc);
  const isPlaying = hover || keepPlaying;

  useEffect(() => {
    setPosterSrc(thumbnailSrc);
  }, [thumbnailSrc]);

  const {
    setRef: setRevealRef,
    activeSrc: revealPosterSrc,
    onLoad: onRevealLoad,
    onError: onRevealError,
    wrapperClassName: revealClass,
    revealed: mediaReady,
  } = useTemplateMediaReveal({
    enabled: isTemplateSite,
    src: posterSrc,
    index: typeof mediaIndex === "number" ? mediaIndex : ctxIndex ?? 0,
    priority: typeof mediaIndex === "number" ? mediaIndex < 6 : false,
  });
  const displayPosterSrc = isTemplateSite ? revealPosterSrc : posterSrc;

  const markTemplateThumbReady = () => {
    onRevealLoad();
  };

  const onPosterError = () => {
    onRevealError();
    const currentIdx = posterCandidates.indexOf(posterSrc);
    const next =
      (currentIdx >= 0 ? posterCandidates[currentIdx + 1] : posterCandidates[0]) ||
      DUMMY_VIDEO_POSTER;
    if (next && next !== posterSrc) {
      setPosterSrc(next);
    } else if (posterSrc !== DUMMY_VIDEO_POSTER) {
      setPosterSrc(DUMMY_VIDEO_POSTER);
    }
  };

  useEffect(() => {
    if (
      navigator.userAgent.search("Safari") >= 0 &&
      navigator.userAgent.search("Chrome") < 0
    ) {
      setShowClickablePlayBtn(true);
    }
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playsInline = true;
    }
  }, []);

  const claimActive = () => {
    if (!postKey || typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent(ACTIVE_VIDEO_EVENT, { detail: { postId: postKey } }),
    );
  };

  useEffect(() => {
    if (!postKey || typeof window === "undefined") return;
    const onActive = (event: Event) => {
      const id = String((event as CustomEvent).detail?.postId || "").trim();
      if (!id || id === postKey) return;
      setHover(false);
      setKeepPlaying(false);
      setCustomPause(false);
      setIsMuted(true);
      const video = videoRef.current;
      if (video) {
        video.muted = true;
        video.pause();
      }
    };
    window.addEventListener(ACTIVE_VIDEO_EVENT, onActive);
    return () => window.removeEventListener(ACTIVE_VIDEO_EVENT, onActive);
  }, [postKey]);

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent click from triggering parent events
    e.preventDefault();
    // Keep playback alive — mute must not act like a pause / hover-end.
    setKeepPlaying(true);
    claimActive();
    setIsMuted((prev) => !prev);
  };

  const getWrapperClassName = () => {
    const baseClass = "creator-feed-image-wrapper-links";
    const classes = [baseClass];

    if (contentShape == "sq") classes.push("square-wrapper");
    // else if (isHasMediaUrl) classes.push("square-wrapper media-feed-wrapper");
    else if (isYoutube) classes.push("youtube-feed-wrapper");
    if (isTemplateSite && revealClass) classes.push(revealClass);
    return classes.join(" ");
  };

  const getVideoClassName = () => {
    const baseClass = "card-image-individual";
    const classes = [baseClass];

    if (contentShape == "sq") classes.push("square-video");
    // else if (isHasMediaUrl) classes.push("media-feed-height");
    else if (isYoutube) classes.push("youtube-feed-height");

    return classes.join(" ");
  };

  const isYouTubePlatform =
    Boolean(isYoutube) ||
    String(post?.platform || "").toLowerCase() === "youtube";

  const OverlayStatus = ({
    hover,
    showClickablePlayBtn,
  }: {
    hover: boolean;
    showClickablePlayBtn: boolean;
  }) => {
    if (isTemplateSite && !mediaReady) return null;
    if (isYouTubePlatform) return null;
    return (
      <>
        {!hover && <BsFillPlayFill className={"creator-feed-card-play-btn"} style={{color:'white'}}/>}
      </>
    );
  };

  // Existing redirect logic, kept as fallback
  const redirectUrl: any = redirectToUrl(post, isLinks);
  // Prefer permalink for media clicks if present
  const permalinkUrl: any = post?.permalink || redirectUrl;

  const openUrlWithTracking = (targetUrl: string | null | undefined) => {
    if (!targetUrl) return;

    handleRedirect(
      post?.post_id || post?.table_id,
      getMediaSrc(undefined, post, false, post?.media_url) ||
        post?.thumbnail ||
        post?.media_url,
      post?.platform,
      type === "content" || type === "query-content"
        ? post?.content_title || post?.product_name
        : post?.shop_title || post?.product_name || post?.table_name,
      title,
      isLinks,
      from
    );

    openOutboundUrl(targetUrl);
  };

  const canShowMedia = Boolean(playableSrc) && (!isTemplateSite || Boolean(displayPosterSrc));

  const posterImg = (
    <Card.Img
      variant="top"
      src={posterSrc}
      className={getVideoClassName()}
      loading="eager"
      decoding="sync"
      onError={onPosterError}
      style={{
        display: "block",
        width: "100%",
        height: "100%",
        objectFit: "cover",
        marginBottom: 0,
      }}
    />
  );

  const mediaBody = !Boolean(playableSrc) ? (
    isTemplateSite ? (
      <div className="mte-tpl-embed-media-placeholder" aria-hidden />
    ) : (
      <div>
        <BsHourglassSplit className="hourglassSplit" />
        <p>
          Work in progress: This content will be available in a few minutes.
        </p>
      </div>
    )
  ) : !canShowMedia ? null : (
    <HoverVideoPlayer
      videoRef={videoRef}
      videoSrc={playableSrc}
      muted={soundEnabled ? isMuted : true}
      focused={isPlaying}
      className={`video-height-individual ${getVideoClassName()}`}
      // One aspect owner (this root). Nested posters must fill it —
      // a second `.video-height-individual` aspect box was the
      // individual-page Shop gap + broken/clipped thumbnails.
      sizingMode="overlay"
      hoverTarget={hoverTargetRef}
      style={{
        display: "block",
        marginBottom: 0,
        width: "100%",
        position: "relative",
        overflow: "hidden",
      }}
      pausedOverlayWrapperStyle={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
      loadingOverlayWrapperStyle={{
        pointerEvents: "none",
      }}
      hoverOverlayWrapperStyle={{
        pointerEvents: "none",
      }}
      videoClassName="individual-hvp-video"
      preload="none"
      contextMenu="return false;"
      hoverOverlay={<div className="video-hover" />}
      pausedOverlay={
        <div
          style={{ position: "relative", display: "block" }}
          className={getVideoClassName()}
        >
          {posterImg}
          <OverlayStatus
            hover={isPlaying}
            showClickablePlayBtn={showClickablePlayBtn}
          />
        </div>
      }
      loadingOverlay={posterImg}
      unloadVideoOnPaused
      onHoverStart={() => {
        setHover(true);
        setKeepPlaying(true);
        claimActive();
      }}
      onHoverEnd={() => {
        setHover(false);
        setCustomPause(false);
        // keepPlaying stays until another post claims active or unmount.
      }}
      disableDefaultEventHandling={customPause}
    />
  );

  return (
    <div className="creator-post-stack" style={{ position: "relative" }}>
      <div
        ref={setRevealRef as React.Ref<HTMLDivElement>}
        className={getWrapperClassName()}
        style={{ position: "relative", border: "none" }}
      >
        {isPinned ? (
          <div className="pinned-badge" aria-label="Pinned post" style={{right:"4px"}}>
            <BsPinAngleFill />
          </div>
        ) : null}

        {isTemplateSite && displayPosterSrc ? (
          <img
            src={displayPosterSrc}
            alt=""
            aria-hidden
            className="mte-tpl-media-reveal-sensor"
            decoding="async"
            onLoad={markTemplateThumbReady}
            onError={onPosterError}
          />
        ) : null}

        <div
          ref={hoverTargetRef}
          className={isTemplateSite ? "mte-tpl-reveal-media-slot" : undefined}
          style={
            contentShape == "sq"
              ? {
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "relative",
                }
              : { cursor: "pointer", position: "relative" }
          }
        >
          <div
            onClick={() => openUrlWithTracking(permalinkUrl)}
            style={{ display: "contents" }}
          >
            {mediaBody}
          </div>
          {Boolean(playableSrc) && canShowMedia && soundEnabled && !isYouTubePlatform ? (
            <button
              type="button"
              className="sound-icon"
              aria-label={isMuted ? "Unmute" : "Mute"}
              style={{
                pointerEvents: "auto",
                appearance: "none",
                border: 0,
                background: "transparent",
                padding: 0,
              }}
              onClick={toggleMute}
            >
              {isMuted ? (
                <BsVolumeMute size={24} color="white" />
              ) : (
                <BsVolumeUp size={24} color="white" />
              )}
            </button>
          ) : null}
        </div>
      </div>

      {!isCarousel && (
        <>
          <ProductName type={type} post={post} designTokens={designTokens} />

          <ProductShopButton
            isLinks={isLinks}
            isIndividual={true}
            type={type}
            post={post}
            designTokens={designTokens}
            onBeforeNavigate={() => {
              // Shop CTA must always track as shop — never inherit carousel isLinks=false.
              handleRedirect(
                post?.post_id || post?.table_id,
                getMediaSrc(undefined, post, false, post?.media_url) ||
                  post?.thumbnail ||
                  post?.media_url,
                post?.platform,
                type === "content" || type === "query-content"
                  ? post?.content_title || post?.product_name
                  : post?.shop_title || post?.product_name || post?.table_name,
                title,
                true,
                from,
              );
            }}
          />
        </>
      )}
    </div>
  );
};

export default IndividualVideoPostTag;
